import React from 'react'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getPropertyCheapestRates,
  arePropertyRatesLoading,
  getFromDurationLabel,
} from 'selectors/offerPage/offerPageSelectors'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import WalledContent from 'components/Common/WalledContent'

import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import Group from 'components/utils/Group'
import OfferPageStickyFooter from '../OfferPageStickyFooter'
import { getHotelOfferDisplayedValue } from 'lib/offer/getOfferDisplayPricing'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import useCheapestPropertyPricing from 'hooks/OfferPage/useCheapestPropertyPricing'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'

interface Props {
  offer: App.HotelOffer;
  onViewPackageOptions: () => void;
  isLoading: boolean;
  durationLabel: string;
  cheapestPropertyRate: App.PropertyRate | undefined;
}

function PropertyStickyFooter(props: Props) {
  const {
    offer,
    onViewPackageOptions,
    isLoading,
    durationLabel,
    cheapestPropertyRate,
  } = props

  const dispatch = useOfferPageDispatch()

  const onViewOptionsClick = () => {
    dispatch({ type: OfferPageActions.closeFilter })
    onViewPackageOptions()
  }

  const cheapestHotelOnlyRate = cheapestPropertyRate
  const [hotelPricing, memberPricing] = useCheapestPropertyPricing()

  const hasHotelPrice = !!(cheapestHotelOnlyRate || hotelPricing)

  const hotelPrice = cheapestHotelOnlyRate?.total.displayPrice ?? hotelPricing?.price ?? 0
  const memberPrice = cheapestHotelOnlyRate?.luxPlusTotal?.displayPrice ?? memberPricing?.price ?? 0

  const value = cheapestHotelOnlyRate?.luxPlusTotal?.valuedUpToPrice || cheapestHotelOnlyRate?.total.valuedUpToPrice || memberPricing?.value || hotelPricing?.value || 0
  const { shouldShowValue, shouldShowDiscountPercent, highestDiscountPercent } = getHotelOfferDisplayedValue(
    // TODO: Checking this LPC offer flag isn't appropriate if we're dealing with an LPP rate.
    !!offer.bundledWithFlightsOnly,
    memberPrice || hotelPrice,
    value,
    !!offer.shouldDisplayValue,
  )

  // Only show LuxPlus+ badging if the member price is _better_ than the non-member price.
  const showMemberPrice = !!memberPrice && memberPrice < hotelPrice

  return (
    <OfferPageStickyFooter
      visible
      callToActionCopy="View options"
      onCTAClick={onViewOptionsClick}
      offer={offer}
    >
      <WalledContent enforced={offer.walledGarden}>
        {hasHotelPrice && hotelPrice > 0 && <Group direction="vertical">
          <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
          <LuxPlusPriceGuard
            showMemberPrice={showMemberPrice}
            fallbackPrice={<PriceRowPrice
              size="S"
              price={hotelPrice}
              saleUnit="total"
            />}
          >
            <PriceRowLuxPlusPrice
              size="M"
              memberPrice={memberPrice}
              saleUnit="total"
              logoPlacement="leading"
            />
          </LuxPlusPriceGuard>
          {shouldShowValue && shouldShowDiscountPercent && <PriceRowValueDiscountWithCaption
            size="M"
            originalValue={value}
            discountPercentage={highestDiscountPercent}
          />}
          <LoadingIndicator visible={isLoading} floating opaque />
        </Group>}
      </WalledContent>
    </OfferPageStickyFooter>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  isLoading: arePropertyRatesLoading(state, appState),
  durationLabel: getFromDurationLabel(state, appState),
  cheapestPropertyRate: getPropertyCheapestRates(state, appState),
  open: !!state.activeFilter,
}), PropertyStickyFooter, preventRenderWithOpenFilter)
